import React, { useState } from 'react';
import ModuleBase from '@/components/general/ModuleBase';
import ReportModulHeader from '@/components/report/ReportModulHeader';
import { colorFromBgColor } from '@/utils/color-utils';
import { CategoryModel, ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import { TrackEventKey } from '@/components/azureAppInsights/AzureAppInsightsContext';
import { motion, AnimatePresence } from 'motion/react';
import { accordionPanel } from '@/animations';
import CaretDownLight from '@/assets/caretDownLight.svg';
import { useRouter } from 'next/router';
import { openLink } from '@/utils/link-utils';
import classNames from 'classnames';
import { Link } from '@/components';

type BrowseByModuleModel = ModuleBaseModel<
  {
    topics?: CategoryModel[];
    demographics?: CategoryModel[];
    innovationPlatforms?: CategoryModel[];
    collapseBrowseBy?: boolean;
  } & ModuleBasePropsModel
>;

const BrowseByModule: React.FC<BrowseByModuleModel> = (data) => {
  const {
    props: { topics, demographics, innovationPlatforms, collapseBrowseBy, backgroundColour },
  } = data;

  const [showAll, setShowAll] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState<{ [key: string]: boolean }>({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const topicsData = !showAll && topics && topics.length > 7 ? topics.slice(0, 7) : topics;

  const demographicData = demographics;
  const innovationPlatformsData = innovationPlatforms;

  const router = useRouter();

  const buildBtnProps = (item: CategoryModel) => {
    return {
      onClick: (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (item.landingPageLink?.href) {
          const linkModel = {
            href: item.landingPageLink.href,
            target: item.landingPageLink.target,
          };
          openLink(router, linkModel);
        }
      },
    };
  };

  const togglePanel = (panelKey: string) => {
    setExpandedPanels((prev) => ({
      ...prev,
      [panelKey]: !prev[panelKey],
    }));
  };

  const CategoryButton = ({
    item,
    className,
    showIcon = false,
  }: {
    item: CategoryModel;
    className?: string;
    showIcon?: boolean;
  }) => {
    const btnProps = buildBtnProps(item);

    return (
      <Link
        className={classNames(
          'text-md flex cursor-pointer items-center rounded-full border border-solid px-4 py-1 transition-all duration-300 ease-in-out hover:bg-[#f5bda2] focus:bg-[#f5bda2]',
          'color-from-bg border-from-bg/20',
          className,
        )}
        link={item.landingPageLink}
        {...btnProps}
      >
        {showIcon && item.icon && <img src={item.icon.imageUrl} width="20" height="20" className="mr-2" alt="" />}
        {item.categoryDisplayName}
      </Link>
    );
  };

  const ExpansionPanel: React.FC<{
    title: string;
    children: React.ReactNode;
    panelKey: string;
  }> = ({ title, children, panelKey }) => {
    const isExpanded = expandedPanels[panelKey] || false;

    return (
      <div className="border-b border-gray-200 last:border-b-0">
        <button
          onClick={() => togglePanel(panelKey)}
          className="flex w-full cursor-pointer items-center justify-between py-5 text-left"
        >
          <span className="text-lg font-medium">{title}</span>
          <CaretDownLight className={`h-6 w-6 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              variants={accordionPanel.variants}
              initial="inactive"
              animate="active"
              exit="inactive"
              className="pb-3"
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <ModuleBase
      data={{
        ...data,
        props: {
          ...data.props,
          // backgroundColour: '#000000'
        },
      }}
      className="bg print:hidden"
    >
      <div className="container">
        <div className="md:hidden">
          <div className="menu-wrapper">
            <div className="relative">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex cursor-pointer items-center justify-between rounded-full border border-solid bg-white px-4 py-2 text-base leading-[22px]"
              >
                <span className="text-base leading-[22px] tracking-[1.25px]">Browse directory by</span>
                <CaretDownLight
                  className={`ml-2 h-6 w-6 transition-transform duration-200 ${mobileMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {mobileMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 z-10 min-w-[350px] rounded-xl bg-white p-4 shadow-[0_5px_5px_-3px_rgba(0,0,0,0.2),0_8px_10px_1px_rgba(0,0,0,0.14),0_3px_14px_2px_rgba(0,0,0,0.12)]"
                  >
                    {topicsData && (
                      <ExpansionPanel title="Topics" panelKey="topics">
                        <div className="space-y-3">
                          {topicsData.map((item) => (
                            <div key={item.categoryCodeName}>
                              <CategoryButton item={item} className="w-full" />
                            </div>
                          ))}
                          {!showAll && topics && topics.length > 7 && (
                            <button
                              className="cursor-pointer pl-0 text-left"
                              style={{
                                color: colorFromBgColor(backgroundColour || '#ffffff') || '#000000',
                              }}
                              onClick={() => setShowAll(true)}
                            >
                              See More
                            </button>
                          )}
                          {showAll && topics && topics.length > 7 && (
                            <button
                              className="cursor-pointer pl-0 text-left"
                              style={{
                                color: colorFromBgColor(backgroundColour || '#ffffff') || '#000000',
                              }}
                              onClick={() => setShowAll(false)}
                            >
                              See Less
                            </button>
                          )}
                        </div>
                      </ExpansionPanel>
                    )}

                    {demographicData && (
                      <ExpansionPanel title="Generations" panelKey="demographic">
                        <div className="space-y-3">
                          {demographicData.map((item) => (
                            <div key={item.categoryCodeName}>
                              <CategoryButton item={item} className="w-full" />
                            </div>
                          ))}
                        </div>
                      </ExpansionPanel>
                    )}

                    {innovationPlatformsData && (
                      <ExpansionPanel title="Pillars of Innovation" panelKey="innovation">
                        <div className="space-y-3">
                          {innovationPlatformsData.map((item) => (
                            <div key={item.categoryCodeName}>
                              <CategoryButton item={item} className="min-h-[44px] w-full" showIcon />
                            </div>
                          ))}
                        </div>
                      </ExpansionPanel>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <ReportModulHeader
          title="Browse directory by"
          expanded={!collapseBrowseBy}
          className="hidden md:block print:hidden"
          eventKey={TrackEventKey.BROWSE_BY}
        >
          <div className="mt-2 grid grid-cols-3 gap-8">
            {/* Topics Column */}
            <div className="py-0">
              <p className="mb-9 text-lg leading-[22px] font-semibold">Topics</p>
              <div className="space-y-3">
                {topicsData?.map((item) => (
                  <div key={item.categoryCodeName} className="flex">
                    <CategoryButton item={item} className="min-h-[32px] py-1" />
                  </div>
                ))}
              </div>
              <div className="pt-7">
                {!showAll && topics && topics.length > 7 && (
                  <button
                    className="btn !text-sm !font-normal underline"
                    style={{
                      height: 'auto',
                      color: colorFromBgColor(backgroundColour || '#ffffff') || '#000000',
                    }}
                    onClick={() => setShowAll(true)}
                  >
                    See More
                  </button>
                )}
                {showAll && topics && topics.length > 7 && (
                  <button
                    className="btn !text-sm !font-normal underline"
                    style={{
                      height: 'auto',
                      color: colorFromBgColor(backgroundColour || '#ffffff') || '#000000',
                    }}
                    onClick={() => setShowAll(false)}
                  >
                    See Less
                  </button>
                )}
              </div>
            </div>

            {/* Demographics Column */}
            <div className="py-0">
              <p className="mb-9 text-lg leading-[22px] font-semibold">Generations</p>
              <div className="flex flex-col gap-3">
                {demographicData?.map((item) => (
                  <div key={item.categoryCodeName} className="flex">
                    <CategoryButton item={item} className="py-1" />
                  </div>
                ))}
              </div>
            </div>

            {/* Innovation Platforms Column */}
            <div className="py-0">
              <p className="mb-9 text-lg leading-[22px] font-semibold">Pillars of Innovation</p>
              <div className="flex flex-col gap-3">
                {innovationPlatformsData?.map((item) => (
                  <div key={item.categoryCodeName} className="flex">
                    <CategoryButton item={item} className="py-2" showIcon />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ReportModulHeader>
      </div>
    </ModuleBase>
  );
};

export default BrowseByModule;
